// Global Instructions Rule Applied!
//
// Options section rendered above the FilterPanel inside the Investments
// screen's Filtering drawer. Mirrors the per-fund InvestmentDetailOptionsPanel
// but adds a multi-fund selector (the standalone screen's fund scope is an
// at__get_measures RPC argument that acts like a filter — empty = all funds).
// State is owned by the hook (session-only, never persisted).

import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox, Select, Space } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs, { type Dayjs } from 'dayjs'
import type { InvestmentDetailOptions } from '../hooks/useInvestmentDetail.ts'

const EMPTY_FUNDS: Array<{ id?: string; name?: string; managed_by_us?: boolean }> = []

interface Props {
  value: InvestmentDetailOptions
  onChange: (next: InvestmentDetailOptions) => void
}

export function InvestmentsOptionsPanel({ value, onChange }: Props): React.ReactElement {
  const funds = useSelector(
    (state: { globalConfigs?: { funds?: Array<{ id?: string; name?: string; managed_by_us?: boolean }> } }) =>
      state.globalConfigs?.funds ?? EMPTY_FUNDS
  )

  const fundOptions = useMemo(
    () =>
      funds
        .filter((f) => f.id && f.name && f.managed_by_us === true)
        .map((f) => ({ value: f.id as string, label: f.name as string })),
    [funds]
  )

  const datetimeValue: Dayjs | null = value.datetime ? dayjs(value.datetime) : null

  const handleDateChange = (d: Dayjs | null) => {
    // End-of-day in the user's TZ — same semantics as the per-fund tab.
    onChange({ ...value, datetime: d ? d.endOf('day').toISOString() : null })
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>Options</div>
      <Space direction='vertical' size={8} className='w-full'>
        <div className='flex flex-col gap-1'>
          <span className='text-xs text-gray-600'>Funds</span>
          <Select
            mode='multiple'
            allowClear
            showSearch
            optionFilterProp='label'
            placeholder='All funds'
            value={value.fundIds}
            options={fundOptions}
            onChange={(ids: string[]) => onChange({ ...value, fundIds: Array.isArray(ids) ? ids : [] })}
            className='w-full'
            maxTagCount='responsive'
          />
        </div>
        <Checkbox
          checked={value.includeWarehoused}
          onChange={(e) => onChange({ ...value, includeWarehoused: e.target.checked })}
        >
          Include warehoused
        </Checkbox>
        <Checkbox
          checked={value.includeConvertedOut}
          onChange={(e) => onChange({ ...value, includeConvertedOut: e.target.checked })}
        >
          Include converted out
        </Checkbox>
        <Checkbox
          checked={value.includeIrr}
          onChange={(e) => onChange({ ...value, includeIrr: e.target.checked })}
        >
          Include IRR <span className='text-gray-400'>(slower)</span>
        </Checkbox>
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-600 min-w-[64px]'>As of:</span>
          <DatePicker
            value={datetimeValue}
            onChange={handleDateChange}
            placeholder='Now'
            format='YYYY-MM-DD'
            allowClear
            className='flex-1'
          />
        </div>
      </Space>
    </div>
  )
}

export default InvestmentsOptionsPanel
